import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axiosService from '../../../services/axios.service';
import MemoVideoPlayer from '../../Common/VideoPlayer/VideoPlayer';
import { isVideoDenied } from '../../../helpers/video-access.helper';

/**
 * Shows a "Watch preview" toggle for a package lesson. The preview is the
 * lesson's first video part (always the free "preview" segment), pulled from
 * the same `/lessons/{id}` endpoint the lesson pages use. Fetched lazily on
 * first open so a package with many lessons doesn't spin up a player per lesson
 * on load.
 */
const LessonPreview = ({ id }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  async function fetchPreview() {
    setIsLoading(true);
    setHasError(false);

    try {
      const res = await axiosService.get(`${process.env.API_URL}/lessons/${id}`);
      const lesson = res.data.data;
      const firstPart = lesson && lesson.videos && lesson.videos.length > 0 ? lesson.videos[0] : null;

      setPreview(firstPart);
    } catch (err) {
      setHasError(true);
    } finally {
      setHasFetched(true);
      setIsLoading(false);
    }
  }

  function toggle() {
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);

    if (nextOpen && !hasFetched) {
      fetchPreview();
    }
  }

  function hasPreview() {
    return preview && preview.platform === 'youtube' && !isVideoDenied(preview);
  }

  return (
    <div className="lesson-preview mt-2">
      <button
        type="button"
        className="btn btn-link p-0 lesson-preview-toggle"
        onClick={toggle}
      >
        <span className={`icon icon-circle ${isOpen ? 'uil-video' : 'uil-play'}`} />
        {isOpen ? 'Hide preview' : 'Watch preview'}
      </button>

      {
        isOpen && (
          <div className="lesson-preview-video mt-2">
            {isLoading && <p className="caption mb-0">Loading preview…</p>}
            {!isLoading && hasPreview() && <MemoVideoPlayer video={preview} />}
            {!isLoading && hasFetched && !hasPreview() && (
              <p className="caption mb-0">{hasError ? 'Preview is unavailable.' : 'No free preview available for this lesson.'}</p>
            )}
          </div>
        )
      }
    </div>
  );
};

LessonPreview.propTypes = {
  id: PropTypes.number,
};

export default LessonPreview;
